import * as React from 'react';

import {
  enumOutboundTollingAgreementSortOption,
  OutboundTollingAgreementSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
  Button,
  Select,
  Switch,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Plus } from 'lucide-react';

import * as state from '../../_state/outboundTollingList.state';
import { $remoteAllProcessingTypes } from '../../_state/processingTypes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { OutboundTollingAgreementRecord } from '../../types/outboundTollingAgreement';

import { useOutboundTollingColumns } from './hooks/useOutboundTollingColumns';
import { OutboundTollingDialog } from './OutboundTollingDialog';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: OutboundTollingAgreementSortOption;
    desc: OutboundTollingAgreementSortOption;
  }
> = {
  agreementNumber: {
    asc: enumOutboundTollingAgreementSortOption.AgreementNumberAsc,
    desc: enumOutboundTollingAgreementSortOption.AgreementNumberDesc,
  },
  effectiveDate: {
    asc: enumOutboundTollingAgreementSortOption.EffectiveDateAsc,
    desc: enumOutboundTollingAgreementSortOption.EffectiveDateDesc,
  },
  expirationDate: {
    asc: enumOutboundTollingAgreementSortOption.ExpirationDateAsc,
    desc: enumOutboundTollingAgreementSortOption.ExpirationDateDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TOLLING LIST VIEW ⚛️ ⚛️ ⚛️
//
export const OutboundTollingListView = () => {
  const t = useTranslations();

  // States
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editingAgreement, setEditingAgreement] =
    React.useState<OutboundTollingAgreementRecord | null>(null);

  // Atoms
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteOutboundTollingAgreements,
  );
  const { data: processingTypesData } = useAtomValue($remoteAllProcessingTypes);

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedProcessingType, setSelectedProcessingType] = useAtom(
    state.$selectedProcessingType,
  );
  const [showInactive, setShowInactive] = useAtom(state.$showInactive);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const outboundTollingAgreements = data?.outboundTollingAgreements ?? [];
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.tollAgreementsView.emptyState.title,
    description: <span>{t.tollAgreementsView.emptyState.subtitle}</span>,
    centered: true,
  };

  // Memoized values
  const processingTypes = React.useMemo(
    () => processingTypesData?.processingTypes ?? [],
    [processingTypesData?.processingTypes],
  );
  const processingTypeOptions = React.useMemo(
    () => [
      { key: 'all', label: t.tollAgreementsView.filters.allProcessingTypes },
      ...processingTypes.map((pt) => ({ key: pt.id, label: pt.name })),
    ],
    [processingTypes, t],
  );

  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([
          enumOutboundTollingAgreementSortOption.CreatedAtDesc,
        ]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleRowClick = React.useCallback(
    ({ row }: { row: OutboundTollingAgreementRecord }) => {
      setEditingAgreement(row);
      setIsDialogOpen(true);
    },
    [],
  );
  const handleCreateClick = React.useCallback(() => {
    setEditingAgreement(null);
    setIsDialogOpen(true);
  }, []);
  const handleDialogClose = React.useCallback(() => {
    setIsDialogOpen(false);
    setEditingAgreement(null);
  }, []);
  const handleDialogSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  // Other data hooks
  const columns = useOutboundTollingColumns({ onRowClick: handleRowClick });

  return (
    <>
      <ListScreenLayout
        filters={
          <>
            <SearchField
              autoFocus
              value={searchTerm ?? ''}
              onChange={(value) => {
                setSearchTerm(value || undefined);
                setOffset(0);
              }}
              placeholder={t.tollAgreementsView.filters.search}
              className="w-64"
            />
            <Select
              value={selectedProcessingType || 'all'}
              onChange={(key) => {
                setSelectedProcessingType(
                  key === 'all' ? undefined : String(key),
                );
                setOffset(0);
              }}
              options={processingTypeOptions}
              className="w-48"
            />
            <div className="flex items-center gap-2">
              <Switch
                isSelected={showInactive ?? false}
                onChange={setShowInactive}
              />
              <span className="text-sm text-neutral/60">
                {t.tollAgreementsView.filters.showInactive}
              </span>
            </div>
          </>
        }
        actions={
          <Button icon={<Plus />} color="primary" onPress={handleCreateClick}>
            {t.tollAgreementsView.createTollAgreement}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<OutboundTollingAgreementRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={outboundTollingAgreements}
          rowKey="id"
          aria-label={t.tollAgreementsView.table.aria}
          onColumnSort={handleColumnSort}
          className="w-full cursor-pointer text-sm"
          fullWidth
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.tollAgreementsView.content.error(
                    error.message,
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <OutboundTollingDialog
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleDialogSuccess}
        editingAgreement={editingAgreement}
      />
    </>
  );
};
